import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Shield, CloudRain, Zap } from "lucide-react";
import { getVesselIGGOverview } from "../../../../services/vesselMachineryService";

const IGGTile = ({ icon, title, value, description, color = "blue" }) => {
  const colorMap = {
    blue: "bg-[#1a53761a] text-[#1a5276]",
    green: "bg-[#27ae601a] text-[#27ae60]",
    orange: "bg-[#d354001a] text-[#d35400]",
    yellow: "bg-[#b7950b1a] text-[#b7950b]",
    red: "bg-[#a932261a] text-[#a93226]",
    purple: "bg-[#7d3c981a] text-[#7d3c98]",
    teal: "bg-[#1178641a] text-[#117864]",
    gray: "bg-[#5665731a] text-[#566573]",
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow h-full">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colorMap[color]}`}>{icon}</div>

        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{title}</h3>
          <p className="text-xl font-bold text-gray-900 mb-2">{value}</p>
        </div>
      </div>

      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );
};

const IGGDataTiles = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);

  const [iggData, setIggData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default data structure
  const defaultIggData = {
    CargoTankInerting: 0,
    PurgeGasFree: 0,
    BlanketingSafety: 0,
    FuelConsumption: 0,
  };

  useEffect(() => {
    const fetchIggData = async () => {
      if (!currentVessel?._id) {
        setIggData(defaultIggData);
        return;
      }

      // Either voyage OR date range must be provided
      if (!selectedVoyage && (!startDate || !endDate)) {
        setIggData(defaultIggData);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselIGGOverview(
          currentVessel._id,
          selectedVoyage ? null : startDate, // Pass null for dates if voyage selected
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null // Pass voyageId if selected
        );

        console.log("API Response IGG:", response);

        if (response?.status === "success") {
          setIggData(response.data || defaultIggData);
        } else {
          setError(response?.message || "Failed to fetch IGG overview data");
          setIggData(defaultIggData);
        }
      } catch (err) {
        console.error("Error fetching IGG data:", err);
        setError(err.message);
        setIggData(defaultIggData);
      } finally {
        setLoading(false);
      }
    };

    fetchIggData();
  }, [currentVessel, startDate, endDate, selectedVoyage]);

  // Calculate total fuel consumption
  const calculateTotalFuelConsumption = () => {
    if (!iggData) return 0;

    return (
      (iggData.CargoTankInerting || 0) +
      (iggData.PurgeGasFree || 0) +
      (iggData.BlanketingSafety || 0)
    );
  };

  const totalFuelConsumption = calculateTotalFuelConsumption();

  // Loading state
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm animate-pulse"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              <div className="w-12 h-6 bg-gray-200 rounded-full"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-6 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="text-yellow-800">
          <p className="font-medium">Unable to load IGG data</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Cargo Tank Inerting Tile */}
      <IGGTile
        icon={<Zap size={20} />}
        title="Cargo Tank Inerting"
        value={`${iggData?.CargoTankInerting || 0} MT`}
        description="Displacing oxygen in cargo tanks to prevent flammable atmospheres"
        color="blue"
        percentage={
          totalFuelConsumption > 0
            ? Math.round(
                ((iggData?.CargoTankInerting || 0) / totalFuelConsumption) * 100
              )
            : 0
        }
      />

      {/* Purge & Gas-Free Operations Tile */}
      <IGGTile
        icon={<CloudRain size={20} />}
        title="Purge & Gas-Free Operations"
        value={`${iggData?.PurgeGasFree || 0} MT`}
        description="Safe atmosphere creation during tank cleaning or gas-freeing"
        color="green"
        percentage={
          totalFuelConsumption > 0
            ? Math.round(
                ((iggData?.PurgeGasFree || 0) / totalFuelConsumption) * 100
              )
            : 0
        }
      />

      {/* Blanketing & Safety Tile */}
      <IGGTile
        icon={<Shield size={20} />}
        title="Blanketing & Safety"
        value={`${iggData?.BlanketingSafety || 0} MT`}
        description="Maintaining positive pressure to avoid air ingress"
        color="orange"
        percentage={
          totalFuelConsumption > 0
            ? Math.round(
                ((iggData?.BlanketingSafety || 0) / totalFuelConsumption) * 100
              )
            : 0
        }
      />
    </div>
  );
};

export default IGGDataTiles;
